/**
 * Leviathan - Fleet Maintenance Page
 * Quasar Prime LLC
 *
 * Track and manage fleet maintenance records
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import {
  Plus,
  Loader2,
  Search,
  Wrench,
  Truck,
  AlertTriangle,
  CheckCircle,
  Clock,
  Calendar,
  DollarSign,
} from 'lucide-react';
import { API_URL } from '@/config/api';

interface MaintenanceRecord {
  id: string;
  truckId: string;
  truckNumber: string;
  truckVin: string | null;
  maintenanceType: string;
  description: string;
  scheduledDate: string;
  completedDate: string | null;
  status: 'SCHEDULED' | 'IN_PROGRESS' | 'COMPLETED' | 'OVERDUE' | 'CANCELLED';
  mileageAtService: number | null;
  nextServiceMileage: number | null;
  nextServiceDate: string | null;
  vendorName: string | null;
  laborCost: number;
  partsCost: number;
  totalCost: number;
  invoiceNumber: string | null;
  notes: string | null;
  performedBy: string | null;
  createdByName: string;
  createdAt: string;
}

interface MaintenanceStats {
  totalRecords: number;
  scheduledCount: number;
  inProgressCount: number;
  completedCount: number;
  overdueCount: number;
  totalLaborCost: number;
  totalPartsCost: number;
  totalCost: number;
  upcomingThisWeek: number;
}

const STATUS_CONFIG = {
  SCHEDULED: {
    label: 'Scheduled',
    color: 'bg-blue-500/20 text-blue-400 border-blue-500/50',
    icon: Calendar,
  },
  IN_PROGRESS: {
    label: 'In Progress',
    color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50',
    icon: Clock,
  },
  COMPLETED: {
    label: 'Completed',
    color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/50',
    icon: CheckCircle,
  },
  OVERDUE: {
    label: 'Overdue',
    color: 'bg-red-500/20 text-red-400 border-red-500/50',
    icon: AlertTriangle,
  },
  CANCELLED: {
    label: 'Cancelled',
    color: 'bg-slate-500/20 text-slate-400 border-slate-500/50',
    icon: Clock,
  },
};

const MAINTENANCE_TYPE_LABELS: Record<string, string> = {
  OIL_CHANGE: 'Oil Change',
  TIRE_SERVICE: 'Tire Service',
  BRAKE_SERVICE: 'Brake Service',
  DOT_INSPECTION: 'DOT Inspection',
  PUMP_CALIBRATION: 'Pump Calibration',
  METER_CALIBRATION: 'Meter Calibration',
  GENERAL_REPAIR: 'General Repair',
  PREVENTIVE: 'Preventive',
  OTHER: 'Other',
};

export default function Maintenance() {
  const { accessToken } = useAuth();
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showNewModal, setShowNewModal] = useState(false);

  // Fetch maintenance stats
  const { data: statsData } = useQuery({
    queryKey: ['maintenance-stats'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/maintenance/stats`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error('Failed to fetch stats');
      return res.json();
    },
  });

  const stats: MaintenanceStats = statsData?.stats || {
    totalRecords: 0,
    scheduledCount: 0,
    inProgressCount: 0,
    completedCount: 0,
    overdueCount: 0,
    totalLaborCost: 0,
    totalPartsCost: 0,
    totalCost: 0,
    upcomingThisWeek: 0,
  };

  // Fetch overdue maintenance
  const { data: overdueData } = useQuery({
    queryKey: ['maintenance-overdue'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/maintenance/overdue`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error('Failed to fetch');
      return res.json();
    },
  });

  const overdueRecords: MaintenanceRecord[] = overdueData?.records || [];

  // Fetch all maintenance records
  const { data, isLoading } = useQuery({
    queryKey: ['maintenance-records', typeFilter, statusFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (typeFilter) params.append('maintenanceType', typeFilter);
      if (statusFilter) params.append('status', statusFilter);

      const res = await fetch(`${API_URL}/api/maintenance?${params}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error('Failed to fetch records');
      return res.json();
    },
  });

  let records: MaintenanceRecord[] = data?.records || [];

  // Client-side search filter
  if (search) {
    const searchLower = search.toLowerCase();
    records = records.filter(
      (r) =>
        r.truckNumber.toLowerCase().includes(searchLower) ||
        r.description.toLowerCase().includes(searchLower) ||
        (r.vendorName && r.vendorName.toLowerCase().includes(searchLower)) ||
        (r.invoiceNumber && r.invoiceNumber.toLowerCase().includes(searchLower))
    );
  }

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-100">Fleet Maintenance</h1>
          <p className="text-slate-400 mt-1">Track and manage truck service records</p>
        </div>
        <button
          onClick={() => setShowNewModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-slate-900 rounded-lg font-medium transition-colors"
        >
          <Plus className="w-4 h-4" />
          New Record
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Scheduled</p>
              <p className="text-2xl font-bold text-blue-400">{stats.scheduledCount}</p>
            </div>
            <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
              <Calendar className="w-5 h-5 text-blue-400" />
            </div>
          </div>
        </div>

        <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">In Progress</p>
              <p className="text-2xl font-bold text-yellow-400">{stats.inProgressCount}</p>
            </div>
            <div className="w-10 h-10 bg-yellow-500/20 rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-yellow-400" />
            </div>
          </div>
        </div>

        <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Overdue</p>
              <p className="text-2xl font-bold text-red-400">{stats.overdueCount}</p>
            </div>
            <div className="w-10 h-10 bg-red-500/20 rounded-lg flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-red-400" />
            </div>
          </div>
        </div>

        <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Completed</p>
              <p className="text-2xl font-bold text-emerald-400">{stats.completedCount}</p>
            </div>
            <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-emerald-400" />
            </div>
          </div>
        </div>

        <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Total Cost</p>
              <p className="text-2xl font-bold text-amber-400">{formatCurrency(stats.totalCost)}</p>
            </div>
            <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-amber-400" />
            </div>
          </div>
        </div>
      </div>

      {/* Overdue Alerts */}
      {overdueRecords.length > 0 && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-5 h-5 text-red-400" />
            <h3 className="font-semibold text-red-400">
              {overdueRecords.length} Overdue Maintenance Record{overdueRecords.length > 1 ? 's' : ''}
            </h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {overdueRecords.slice(0, 6).map((record) => (
              <div
                key={record.id}
                className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-3"
              >
                <div className="flex items-center gap-2">
                  <Truck className="w-4 h-4 text-slate-400" />
                  <span className="text-slate-100 font-medium">{record.truckNumber}</span>
                </div>
                <div className="text-slate-400 text-sm mt-1">
                  {MAINTENANCE_TYPE_LABELS[record.maintenanceType] || record.maintenanceType}
                </div>
                <div className="text-red-400 text-xs mt-1">
                  Due: {formatDate(record.scheduledDate)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="relative flex-1 min-w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by truck, description, vendor..."
            className="w-full pl-10 pr-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
          />
        </div>

        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
        >
          <option value="">All Types</option>
          {Object.entries(MAINTENANCE_TYPE_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
        >
          <option value="">All Status</option>
          {Object.entries(STATUS_CONFIG).map(([value, config]) => (
            <option key={value} value={value}>
              {config.label}
            </option>
          ))}
        </select>
      </div>

      {/* Maintenance Records Table */}
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
          </div>
        ) : records.length === 0 ? (
          <div className="text-center py-12">
            <Wrench className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400 mb-4">No maintenance records found</p>
            <button
              onClick={() => setShowNewModal(true)}
              className="text-amber-400 hover:text-amber-300"
            >
              Create your first record
            </button>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-slate-900/50 border-b border-slate-700">
              <tr>
                <th className="text-left px-4 py-3 text-slate-500 text-xs uppercase tracking-wider">
                  Truck
                </th>
                <th className="text-left px-4 py-3 text-slate-500 text-xs uppercase tracking-wider">
                  Type / Description
                </th>
                <th className="text-center px-4 py-3 text-slate-500 text-xs uppercase tracking-wider">
                  Status
                </th>
                <th className="text-center px-4 py-3 text-slate-500 text-xs uppercase tracking-wider">
                  Scheduled
                </th>
                <th className="text-center px-4 py-3 text-slate-500 text-xs uppercase tracking-wider">
                  Completed
                </th>
                <th className="text-left px-4 py-3 text-slate-500 text-xs uppercase tracking-wider">
                  Vendor
                </th>
                <th className="text-right px-4 py-3 text-slate-500 text-xs uppercase tracking-wider">
                  Mileage
                </th>
                <th className="text-right px-4 py-3 text-slate-500 text-xs uppercase tracking-wider">
                  Cost
                </th>
              </tr>
            </thead>
            <tbody>
              {records.map((record) => {
                const statusConfig = STATUS_CONFIG[record.status] || STATUS_CONFIG.SCHEDULED;
                const StatusIcon = statusConfig.icon;

                return (
                  <tr
                    key={record.id}
                    className="border-b border-slate-700/50 hover:bg-slate-800/50 cursor-pointer transition-colors"
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Truck className="w-4 h-4 text-slate-400" />
                        <span className="text-amber-400 font-mono font-semibold">
                          {record.truckNumber}
                        </span>
                      </div>
                      {record.truckVin && (
                        <div className="text-slate-500 text-xs mt-0.5">{record.truckVin}</div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-slate-100 font-medium">
                        {MAINTENANCE_TYPE_LABELS[record.maintenanceType] || record.maintenanceType}
                      </div>
                      <div className="text-slate-400 text-sm truncate max-w-xs">
                        {record.description}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs border rounded ${statusConfig.color}`}
                      >
                        <StatusIcon className="w-3 h-3" />
                        {statusConfig.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center text-slate-400 text-sm">
                      {formatDate(record.scheduledDate)}
                    </td>
                    <td className="px-4 py-3 text-center text-slate-400 text-sm">
                      {formatDate(record.completedDate)}
                    </td>
                    <td className="px-4 py-3">
                      {record.vendorName ? (
                        <div>
                          <div className="text-slate-300 text-sm">{record.vendorName}</div>
                          {record.invoiceNumber && (
                            <div className="text-slate-500 text-xs">Inv: {record.invoiceNumber}</div>
                          )}
                        </div>
                      ) : (
                        <span className="text-slate-500 text-sm">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {record.mileageAtService ? (
                        <div>
                          <div className="text-slate-100">
                            {record.mileageAtService.toLocaleString()}
                          </div>
                          {record.nextServiceMileage && (
                            <div className="text-slate-500 text-xs">
                              Next: {record.nextServiceMileage.toLocaleString()}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-slate-500">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {record.totalCost > 0 ? (
                        <div>
                          <div className="text-amber-400 font-semibold">
                            {formatCurrency(record.totalCost)}
                          </div>
                          <div className="text-slate-500 text-xs">
                            L: {formatCurrency(record.laborCost)} | P: {formatCurrency(record.partsCost)}
                          </div>
                        </div>
                      ) : (
                        <span className="text-slate-500">-</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* New Record Modal Placeholder */}
      {showNewModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 w-full max-w-lg">
            <h2 className="text-xl font-bold text-slate-100 mb-4">New Maintenance Record</h2>
            <p className="text-slate-400 mb-6">
              Maintenance record form will be implemented in a future update.
            </p>
            <div className="flex justify-end">
              <button
                onClick={() => setShowNewModal(false)}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-100 rounded-lg transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
